import { DateFilterCondition, NumericFilterCondition, TextFilterCondition } from './filterV2.types';
import { CheckboxOption } from './filters/CheckboxFilter';

const textConditions = {
    eq: 'Equals',
    ne: 'Not Equal To',
    bw: 'Begins With',
    ew: 'Ends With',
    con: 'Contains',
    nco: 'Does Not Contain',
};

const numericConditions = {
    eq: '=',
    ne: '≠',
    lt: '<',
    gt: '>',
    lte: '<=',
    gte: '>=',
    btw: 'Between',
};

const dateConditions = {
    eq: 'Equal to',
    lt: 'Before',
    gt: 'After',
    btw: 'Between',
};

/**
 * Checks if a condition can be used to filter text.
 *
 * @param str
 * @returns str is TextFilterCondition
 */
export function isTextFilterCondition(str: string): str is TextFilterCondition {
    return Object.keys(textConditions).includes(str);
}

/**
 * Checks if a condition can be used to filter numbers.
 *
 * @param str
 * @returns str is NumericFilterCondition
 */
export function isNumericFilterCondition(str: string): str is NumericFilterCondition {
    return Object.keys(numericConditions).includes(str);
}

/**
 * Checks if a condition can be used to filter dates.
 *
 * @param str
 * @returns str is DateFilterCondition
 */
export function isDateFilterCondition(str: string): str is DateFilterCondition {
    return Object.keys(dateConditions).includes(str);
}

/**
 * Removes all text conditions for a filter from the url.
 *
 * @param id Filter id
 * @param search URLSearchParams
 * @returns URLSearchParams
 */
export function clearTextSearch(id: string, search: URLSearchParams): URLSearchParams {
    for (const k in textConditions) {
        search.delete(`${id}[${k}]`);
    }
    return search;
}

/**
 * Removes all numeric conditions for a filter from the url.
 *
 * @param id Filter id
 * @param search URLSearchParams
 * @returns URLSearchParams
 */
export function clearNumericSearch(id: string, search: URLSearchParams): URLSearchParams {
    for (const k in numericConditions) {
        search.delete(`${id}[${k}]`);
    }
    return search;
}

/**
 * Removes all date conditions for a filter from the url.
 *
 * @param id Filter id
 * @param search URLSearchParams
 * @returns URLSearchParams
 */
export function clearDateSearch(id: string, search: URLSearchParams): URLSearchParams {
    for (const k in dateConditions) {
        search.delete(`${id}[${k}]`);
    }
    return search;
}

/**
 * Returns a human-friendly label for a text filter condition.
 *
 * @param condition TextFilterCondition
 * @returns
 */
export function getTextConditionLabel(condition: TextFilterCondition): string {
    return textConditions[condition];
}

/**
 * Returns a human-friendly label for a numeric filter condition.
 *
 * @param condition NumericFilterCondition
 * @returns
 */
export function getNumericConditionLabel(condition: NumericFilterCondition): string {
    return numericConditions[condition];
}

/**
 * Returns a human-friendly label for a date filter condition.
 *
 * @param condition DateFilterCondition
 * @returns
 */
export function getDateConditionLabel(condition: DateFilterCondition): string {
    return dateConditions[condition];
}

export function sanitizeCheckboxQueryString(id: string, options: CheckboxOption[], query: qs.ParsedQs): string[] {
    let saved = query[id] as string[];
    if (!saved) {
        return [];
    }

    if (Array.isArray(saved) && saved.every((s) => typeof s === 'string')) {
        saved = [...saved];
    } else if (typeof saved === 'string') {
        saved = [saved];
    } else {
        saved = [];
    }

    const valid = saved.filter((s) => options.find((o) => o.value === s));
    return valid;
}

export function sanitizeTextQueryString(id: string, query: qs.ParsedQs): { condition: TextFilterCondition; values: string[] } {
    let condition: TextFilterCondition = 'eq';
    let values: string[] = [];

    const saved = query[id];

    if (!saved) {
        return { condition: 'eq', values: [] };
    }

    if (!Array.isArray(saved) && typeof saved !== 'string') {
        const keys = Object.keys(saved);

        if (isTextFilterCondition(keys[0])) {
            condition = keys[0];
            const vals = saved[condition] as string[];

            if (Array.isArray(vals) && vals.every((v) => typeof v === 'string')) {
                values = vals;
            } else if (typeof vals === 'string') {
                values = [vals];
            }
        } else {
            condition = 'eq';
            values = [];
        }
    }
    return { condition, values };
}

export function sanitizeNumericQueryString(id: string, query: qs.ParsedQs): { condition: NumericFilterCondition; values: number[] } {
    let condition: NumericFilterCondition = 'eq';
    let values: number[] = [];

    const saved = query[id];

    if (!saved) {
        return { condition: 'eq', values: [] };
    }

    if (!Array.isArray(saved) && typeof saved !== 'string') {
        const keys = Object.keys(saved);

        // Special case for between
        if (keys.length === 2 && keys.includes('lte') && keys.includes('gte')) {
            const val1 = saved.gte as string;
            const val2 = saved.lte as string;

            if (!isNaN(Number(val1)) && !isNaN(Number(val2))) {
                values = [Number(val1), Number(val2)];
                condition = 'btw';
            }
        } else if (isNumericFilterCondition(keys[0])) {
            condition = keys[0];
            const vals = saved[condition] as string[];

            if (Array.isArray(vals) && vals.every((v) => typeof v === 'string')) {
                values = vals.filter((v) => !isNaN(Number(v))).map((v) => Number(v));
            } else if (typeof vals === 'string') {
                const maybeNumber = Number(vals);
                if (!isNaN(maybeNumber)) values = [maybeNumber];
            }
        } else {
            condition = 'eq';
            values = [];
        }
    }

    return { condition, values };
}

export function sanitizeDateQueryString(id: string, query: qs.ParsedQs): { condition: DateFilterCondition; values: number[] } {
    let condition: DateFilterCondition = 'eq';
    let values: number[] = [];

    const saved = query[id];

    if (!saved) {
        return { condition: 'eq', values: [] };
    }

    if (!Array.isArray(saved) && typeof saved !== 'string') {
        const keys = Object.keys(saved);

        // Special case for between
        if (keys.length === 2 && keys.includes('lte') && keys.includes('gte')) {
            const val1 = saved.gt as string;
            const val2 = saved.lt as string;

            if (!isNaN(Number(val1)) && !isNaN(Number(val2))) {
                values = [Number(val1), Number(val2)];
                condition = 'btw';
            }
        } else if (isDateFilterCondition(keys[0])) {
            condition = keys[0];
            const vals = saved[condition] as string[];

            if (Array.isArray(vals) && vals.every((v) => typeof v === 'string')) {
                values = vals.filter((v) => !isNaN(Number(v))).map((v) => Number(v));
            } else if (typeof vals === 'string') {
                const maybeNumber = Number(vals);
                if (!isNaN(maybeNumber)) values = [maybeNumber];
            }
        } else {
            condition = 'eq';
            values = [];
        }
    }

    return { condition, values };
}

export function sanitizeToggleQueryString(id: string, query: qs.ParsedQs): boolean | undefined {
    const saved = query[id];

    if (saved === 'true') {
        return true;
    }
    if (saved === 'false') {
        return false;
    }
    return undefined;
}

export function sanitizeSelectQueryString(id: string, query: qs.ParsedQs): string[] {
    let saved = query[id] as string[];
    if (!saved) {
        return [];
    }

    if (Array.isArray(saved) && saved.every((s) => typeof s === 'string')) {
        saved = [...saved];
    } else if (typeof saved === 'string') {
        saved = [saved];
    } else {
        saved = [];
    }

    return saved;
}
